import { useEffect, useRef } from "react";

const steps = [
  {
    number: "01",
    icon: "ri-home-4-line",
    title: "Homeowner Submits Lead",
    description:
      "Homeowners complete a detailed form with their address, monthly bill, and timeline. Every submission is scored and categorized.",
  },
  {
    number: "02",
    icon: "ri-sun-line",
    title: "Lead Scoring Applied",
    description:
      "Each lead receives a Lead Score (quality indicators) and Solar Score (savings potential) from 50-99. Higher scores = better opportunities.",
  },
  {
    number: "03",
    icon: "ri-store-2-line",
    title: "Browse Live Marketplace",
    description:
      "Log into your dashboard to see available leads in real-time. Filter by state, price range ($100-$200), and minimum score to find your perfect match.",
  },
  {
    number: "04",
    icon: "ri-shopping-bag-line",
    title: "Purchase & Manage",
    description:
      "Buy leads instantly with Stripe checkout. Track them through your pipeline, add notes, and manage everything in one place.",
  },
];

export default function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".step-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = "1";
                (card as HTMLElement).style.transform = "translateY(0)";
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-green-600 text-xs font-bold uppercase tracking-widest mb-3 block">The Process</span>
          <h2 className="text-3xl md:text-5xl font-bold text-black tracking-tight">How It Works</h2>
          <p className="mt-4 text-gray-500 text-lg max-w-xl mx-auto">
            From homeowner curiosity to a qualified lead in your inbox — here&apos;s the flow.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-6 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden lg:block absolute top-16 left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-px border-t-2 border-dashed border-green-200 z-0"></div>

          {steps.map((step) => (
            <div
              key={step.number}
              className="step-card relative bg-white border border-gray-100 rounded-2xl p-7 hover:border-green-200 transition-all duration-300 cursor-default"
              style={{ opacity: 0, transform: "translateY(32px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}
            >
              {/* Big number */}
              <span className="absolute top-5 right-5 text-5xl font-black text-green-50 select-none leading-none">
                {step.number}
              </span>

              {/* Icon */}
              <div className="w-13 h-13 w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl mb-5 relative z-10">
                <i className={`${step.icon} text-2xl text-green-600`}></i>
              </div>

              {/* Content */}
              <h3 className="text-lg font-bold text-black mb-2 relative z-10">{step.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed relative z-10">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
